"use strict";
const ALL = require('./constants');
const { PLATFORMS, ENV } = ALL;
const APP_DEVELOPMENT_HOST = 'app.famly.test';
// .de is on purpose first to make sure we don't send data from german customers to .co domain
const extraTLDs = ['.de'];
const baseTLD = '.co';
const allTLDs = [...extraTLDs, baseTLD];
const brightHorizonsHostname = 'familyapp.brighthorizons.co.uk';
const brightHorizonsImgService = `https://img.${brightHorizonsHostname}`;
const brightHorizonsApiService = `https://famlyapi.${brightHorizonsHostname}`;
const busyBeesHostname = 'app.busybees.com';
const busyBeesImgService = `https://img.${busyBeesHostname}`;
const busyBeesApiService = `https://famlyapi.${busyBeesHostname}`;
const skoHostname = 'famly.skocloud.nl';
const skoImgService = `https://img.${skoHostname}`;
const skoApiService = `https://famlyapi.${skoHostname}`;
const determineMobileApp = platform => platform === PLATFORMS.ANDROID || platform === PLATFORMS.IOS;
// The automation engine is a standalone service with one deployment per environment
// (not per TLD like the main Famly API). Dev machines run it locally on port 3000
// — see automation-engine/README.md.
const automationEngineUrlForHost = host => {
    if (!host) {
        return 'http://localhost:3000';
    }
    if (host.includes('tryfamly')) {
        return 'https://automation-engine.tryfamly.co';
    }
    return 'https://automation-engine.famly.co';
};
const automationEngineStagingUrl = 'https://automation-engine.tryfamly.co';
const automationEngineProductionUrl = 'https://automation-engine.famly.co';
// The new Reporting API, for configs whose backend is not the host serving the app. It is per TLD like
// the API itself, so German data stays on .de. Production has no deployment yet; its host is the one it is
// planned under, so builds reach it once it exists.
const reporting2StagingUrl = tld => `https://reportingv2.tryfamly${tld}`;
const reporting2ProductionUrl = tld => `https://reportingv2.famly${tld}`;
// A key, not an undefined value: the CSP builder rejects config keys without a string value.
const reporting2ConfigForHost = host => {
    if (!host) {
        return {};
    }
    if (host.includes('tryfamly')) {
        return { reporting2: reporting2StagingUrl(baseTLD) };
    }
    return /(^|\.)famly\.(co|de)$/.test(host) ? { reporting2: reporting2ProductionUrl(baseTLD) } : {};
};
// Staging for the mobile app, both in staging builds and as the runtime target of production builds
const mobileAppStagingTestingConfigs = () => allTLDs.map(tld => ({
    key: tld,
    isBase: tld === baseTLD,
    app: `https://app.tryfamly${tld}`,
    api: `https://api.tryfamly${tld}/`,
    famlyapi: `https://famlyapi.tryfamly${tld}/famlyapi`,
    graphql: `https://famlyapi.tryfamly${tld}/graphql`,
    automationEngine: automationEngineStagingUrl,
    reporting2: reporting2StagingUrl(tld),
}));
const getAPIConfigs = (env, platform, isDevelopment, whitelabelAppId, remoteDevHost) => {
    const isWeb = platform === PLATFORMS.WEB;
    const isMobileApp = determineMobileApp(platform);
    const appDevelopmentUrl = remoteDevHost ? `https://${remoteDevHost}` : `http://${APP_DEVELOPMENT_HOST}`;
    const targetOrigin = (isMobileApp && isDevelopment) || remoteDevHost ? appDevelopmentUrl : '';
    const localConfig = {
        key: '.local',
        isBase: true,
        app: '/',
        api: `${targetOrigin}/api`,
        famlyapi: `${targetOrigin}/famlyapi`,
        graphql: `${targetOrigin}/graphql`,
        automationEngine: automationEngineUrlForHost(remoteDevHost),
        ...reporting2ConfigForHost(remoteDevHost),
    };
    const stagingTestingConfigs = allTLDs.map(tld => {
        const isBase = tld === baseTLD;
        return {
            key: tld,
            isBase,
            app: isWeb && isDevelopment ? appDevelopmentUrl : `https://app.tryfamly${tld}`,
            api: isBase ? `https://tryfamly${tld}/api` : `https://app.tryfamly${tld}/api`,
            famlyapi: isBase ? `https://tryfamly${tld}/famlyapi` : `https://app.tryfamly${tld}/famlyapi`,
            graphql: isBase ? `https://tryfamly${tld}/graphql` : `https://app.tryfamly${tld}/graphql`,
            automationEngine: automationEngineStagingUrl,
            reporting2: reporting2StagingUrl(tld),
        };
    });
    const mobileAppBrightHorizonsConfig = [
        {
            key: '.co.uk',
            isBase: true,
            app: `https://${brightHorizonsHostname}`,
            api: `https://api.${brightHorizonsHostname}`,
            famlyapi: `https://famlyapi.${brightHorizonsHostname}/famlyapi`,
            graphql: `https://famlyapi.${brightHorizonsHostname}/graphql`,
            automationEngine: automationEngineProductionUrl,
        },
    ];
    const mobileAppBusybeesConfig = [
        {
            key: '.com',
            isBase: true,
            app: `https://${busyBeesHostname}`,
            api: `https://api.${busyBeesHostname}`,
            famlyapi: `https://famlyapi.${busyBeesHostname}/famlyapi`,
            graphql: `https://famlyapi.${busyBeesHostname}/graphql`,
            automationEngine: automationEngineProductionUrl,
        },
    ];
    switch (env) {
        case ENV.TEST:
        case ENV.LOCAL:
            return [localConfig];
        // The German Azure setup runs docker in production mode on staging
        case ENV.STAGING:
            // We have two staging builds. One for Azure that uses docker together with our old CloudFront/S3 setup
            if (platform === PLATFORMS.DOCKER || (isWeb && !isDevelopment)) {
                return [{ ...localConfig, automationEngine: automationEngineStagingUrl }];
            }
            // ...and one for development and the mobile app
            else if (isMobileApp) {
                return mobileAppStagingTestingConfigs();
            }
            else {
                return stagingTestingConfigs;
            }
        case ENV.PRODUCTION: {
            // We have three production builds. One for Azure that uses docker together with our old CloudFront/S3 setup
            if (platform === PLATFORMS.DOCKER || (isWeb && !isDevelopment)) {
                return [{ ...localConfig, automationEngine: automationEngineProductionUrl }];
            }
            // ...one for the mobile apps apps that circumvent CloudFront to save $$$ as they don't have to worry about CORS
            else if (isMobileApp) {
                switch (whitelabelAppId) {
                    case 'brighthorizons':
                        return mobileAppBrightHorizonsConfig;
                    case 'busybees':
                    case 'bbmontessori':
                    case 'bbodyssey':
                        return mobileAppBusybeesConfig;
                    default:
                        return allTLDs.map(tld => ({
                            key: tld,
                            isBase: tld === baseTLD,
                            app: isWeb && isDevelopment ? appDevelopmentUrl : `https://app.famly${tld}`,
                            api: `https://api.famly${tld}/api`,
                            famlyapi: `https://famlyapi.famly${tld}/famlyapi`,
                            graphql: `https://famlyapi.famly${tld}/graphql`,
                            automationEngine: automationEngineProductionUrl,
                            reporting2: reporting2ProductionUrl(tld),
                        }));
                }
            }
            // ...and one for development
            else {
                return allTLDs.map(tld => ({
                    key: tld,
                    isBase: tld === baseTLD,
                    app: isWeb && isDevelopment ? appDevelopmentUrl : `https://app.famly${tld}`,
                    api: `https://app.famly${tld}/api`,
                    famlyapi: `https://app.famly${tld}/famlyapi`,
                    graphql: `https://app.famly${tld}/graphql`,
                    automationEngine: automationEngineProductionUrl,
                    reporting2: reporting2ProductionUrl(tld),
                }));
            }
        }
        default:
            throw new Error(`Unknown environment: ${env}`);
    }
};
/**
 * Backends a build can be switched to at runtime, from the debugger page, instead of the ones
 * `getAPIConfigs` bakes in. Only the Famly production mobile app gets any: the store binary is the
 * one we need to test against staging, and whitelabels have no staging hosts of their own.
 */
const getServerTargets = (env, platform, whitelabelAppId) => {
    if (env !== ENV.PRODUCTION || !determineMobileApp(platform) || whitelabelAppId) {
        return {};
    }
    return { staging: mobileAppStagingTestingConfigs() };
};
exports.getAPIConfigs = getAPIConfigs;
exports.getServerTargets = getServerTargets;
exports.determineMobileApp = determineMobileApp;
exports.APP_DEVELOPMENT_HOST = APP_DEVELOPMENT_HOST;
exports.allTLDs = allTLDs;
exports.brightHorizonsHostname = brightHorizonsHostname;
exports.brightHorizonsImgService = brightHorizonsImgService;
exports.brightHorizonsApiService = brightHorizonsApiService;
exports.busyBeesHostname = busyBeesHostname;
exports.busyBeesImgService = busyBeesImgService;
exports.busyBeesApiService = busyBeesApiService;
exports.skoImgService = skoImgService;
exports.skoApiService = skoApiService;
